import {
  Button,
  ButtonProps,
  CircularProgress,
  CircularProgressProps,
} from '@mui/material';
import { ReactNode } from 'react';
import styled from '@mui/material/styles/styled';

function addAlpha(color: string, alpha: string) {
  const alphaColor = color.replace(/[^,]+(?=\))/, alpha);
  return alphaColor;
}

const BUTTON_COLOR = {
  PRIMARY: 'rgba(0, 26, 161, 1)',
  SUCCESS: 'rgba(18, 178, 140, 1)',
};

interface VegaPrimaryButtonProps extends StyledButtonProps {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  text: string;
  loading?: boolean;
}

interface StyledLoaderProps extends CircularProgressProps {
  buttonVariant?: string;
}

interface StyledButtonProps extends ButtonProps {
  isSuccess?: boolean;
}

const StyledButton = styled(Button)<StyledButtonProps>(
  ({ theme, variant = 'contained', isSuccess }) => {
    const commonStyles = {
      borderRadius: '0.25rem',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&.Mui-disabled': {
        backgroundColor: '#293543',
        color: '#FFFFFF',
      },
    };

    const containedStyles = {
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      backgroundColor: isSuccess ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.PRIMARY,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
      '&.Mui-disabled': {
        backgroundColor: '#293543',
        color: '#FFFFFF',
      },
    };

    const outlinedStyles = {
      backgroundColor: 'transparent',
      border: `1px solid ${
        isSuccess ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.PRIMARY
      }`,
      color: isSuccess ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.PRIMARY,
      '&:hover': {
        border: `1px solid ${
          isSuccess ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.PRIMARY
        }`,
        backgroundColor: addAlpha(
          isSuccess ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.PRIMARY,
          '1'
        ),
        color: 'white',
        '& .MuiButton-startIcon svg path': {
          fill: 'white',
        },
      },
      '&.Mui-disabled': {
        color: '#293543',
        border: '1px solid #293543',
      },
    };

    const variantStyles =
      variant === 'contained'
        ? containedStyles
        : variant === 'outlined'
        ? outlinedStyles
        : {};

    return {
      ...commonStyles,
      ...variantStyles,
    };
  }
);

const StyledLoader = styled(CircularProgress)<StyledLoaderProps>(
  ({ theme, buttonVariant = 'contained', size }) => {
    if (buttonVariant == 'contained') {
      return {
        size: size,
        color: 'white',
      };
    }
    return {
      size: size,
      color: '#001aa1',
    };
  }
);

function VegaPrimaryButton({
  onClick,
  startIcon,
  endIcon,
  text,
  loading = false,
  isSuccess,
  ...rest
}: VegaPrimaryButtonProps) {
  return (
    <StyledButton
      {...rest}
      isSuccess={isSuccess}
      disabled={rest.disabled || loading}
      startIcon={
        loading ? (
          <StyledLoader size={24} buttonVariant={rest.variant} />
        ) : (
          startIcon
        )
      }
      endIcon={endIcon}
      onClick={onClick}
    >
      {text}
    </StyledButton>
  );
}

export default VegaPrimaryButton;
